import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardBody, Container, Row, Col, Table, Input, Button, Spinner, Label, FormGroup } from "reactstrap";
import { FaSearch, FaDownload, FaFilter } from "react-icons/fa";
import { formatDate, formatPrice } from "../../helpers/utility";
import { commercialActions } from "../../sagas/commercialSlice";
import Select from "react-select";
import {
    ResponsiveContainer,
    BarChart, Bar,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from "recharts";
import { FaChartBar } from "react-icons/fa";
import { useMemo } from "react";
import dayjs from "dayjs";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";

const CommissionHistoryPage = () => {
    const dispatch = useDispatch();
    const { ledgerLoading, list, ledgerByCommercialId } = useSelector((s) => s.commercials);
    const { exportLoading } = useSelector(s => s.commercials);


    const [filters, setFilters] = useState({
        commercialId: null,
        period: "DAYS_30",
        refDate: dayjs().format("YYYY-MM-DD"),
    });

    const [data, setData] = useState([]);

    const [loading, setLoading] = useState(false);

    // Load all commercials for the filter dropdown
    const commercials = useSelector((s) => s.commercials.list);

    const chartData = useMemo(() => {
        // On ne garde que les écritures POSTED pour la synthèse
        const posted = (data || []).filter(r => r.status === "POSTED");
        const byCommercial = new Map();

        for (const r of posted) {
            const key = r.commercialName || `#${r.commercialId}`;
            const prev = byCommercial.get(key) || { commercial: key, baseCommission: 0, bonusCommission: 0, totalCommission: 0 };
            prev.baseCommission += Number(r.baseCommission || 0);
            prev.bonusCommission += Number(r.bonusCommission || 0);
            prev.totalCommission = prev.baseCommission + prev.bonusCommission;
            byCommercial.set(key, prev);
        }

        // Tri décroissant par total
        return Array.from(byCommercial.values()).sort((a, b) => b.totalCommission - a.totalCommission);
    }, [data]);


    useEffect(() => {
        if (commercials.length === 0) {
            dispatch(commercialActions.list({}));
        }

        dispatch(commercialActions.listLedgerEffective({
            commercialId: filters.commercialId,
            period: filters.period,
            refDate: filters.refDate,
            onSuccess: (data) => {
                setData(data);
                setLoading(false);
            },
            onError: () => setLoading(false),
        })
        );
    }, []);

    const handleFilter = useCallback(() => {
        setLoading(true);
        dispatch(
            commercialActions.listLedgerEffective({
                commercialId: filters.commercialId,
                period: filters.period,
                refDate: filters.refDate,
                onSuccess: (data) => {
                    setData(data);
                    setLoading(false);
                },
                onError: () => setLoading(false),
            })
        );
    }, [filters]);

    const handleExport = async () => {
        // 2) Compute totals
        const totals = data.reduce(
            (acc, r) => {
                acc.base += Number(r.baseCommission || 0);
                acc.bonus += Number(r.bonusCommission || 0);
                acc.total += Number(r.totalCommission || 0);
                return acc;
            },
            { base: 0, bonus: 0, total: 0 }
        );

        // 3) Build PDF
        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        // Header
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.text("Historique des commissions — Effectif", 40, 40);
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        const filterLine = `Période: ${data[0].periodStart || "-"} - ${data[0].periodEnd || "-"} `;
        doc.text(filterLine, 40, 60);

        // Table
        const head = [[
            "#",
            "Commercial",
            "Période",
            "Base utilisée",
            "Commission base",
            "Bonus",
            "Total",
            "Posté le",
            "Ref",
        ]];

        const body = (data || []).map((r, idx) => ([
            idx + 1,
            r.commercialName,
            `${r.periodStart} → ${r.periodEnd}`,
            r.baseType === "ON_REVENUE" ? "CA" : "Marge",
            formatPrice(r.baseCommission),
            formatPrice(r.bonusCommission),
            formatPrice(r.totalCommission),
            (r.postedAt || "").replace("T", " ").slice(0, 16),
            r.postingRef || "",
        ]));

        autoTable(doc, {
            startY: 85,
            head,
            body,
            styles: { font: "helvetica", fontSize: 9, cellPadding: 4, overflow: "linebreak" },
            headStyles: { fillColor: [30, 58, 138], textColor: 255 }, // bleu royal
            alternateRowStyles: { fillColor: [245, 247, 250] },
            margin: { left: 40, right: 40 },
            didDrawPage: (data) => {
                const page = doc.internal.getNumberOfPages();
                const ts = dayjs().format("YYYY-MM-DD HH:mm");
                doc.setFontSize(9);
                doc.setTextColor(120);
                doc.text(`Généré le ${ts}`, 40, pageHeight - 20);
                doc.text(`Page ${page}`, pageWidth - 80, pageHeight - 20);
            },
        });

        // Totals block
        const y = (doc.lastAutoTable?.finalY || 85) + 24;
        doc.setFont("helvetica", "bold"); doc.setFontSize(11);
        doc.text("Totaux (effectifs) :", 40, y);
        doc.setFont("helvetica", "normal");
        doc.text(`Commission base : ${formatPrice(totals.base)}`, 150, y);
        doc.text(`Bonus : ${formatPrice(totals.bonus)}`, 380, y);
        doc.text(`Total : ${formatPrice(totals.total)}`, 580, y);

        const pdfBlob = doc.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl);

    };

    const totalPosted = useMemo(() => {
        return (data || [])
            .filter(r => r.status === "POSTED")
            .reduce((acc, r) => acc + Number(r.totalCommission || 0), 0);
    }, [data]);

    return (
        <Container fluid className="mt-3">
            <h4 className="mb-3"><FaSearch className="text-primary me-2" /> Historique des commissions</h4>

            <Card className="shadow-sm">
                <CardBody>
                    <Row className="mb-3 align-items-end">
                        <Col md={4}>
                            <FormGroup>
                                <Label>Commercial</Label>
                                <Select
                                    options={[
                                        { value: "", label: "Tous les commerciaux" },
                                        ...commercials.map((c) => ({ value: c.id, label: c.fullName })),
                                    ]}
                                    onChange={(opt) =>
                                        setFilters((p) => ({ ...p, commercialId: opt?.value || null }))
                                    }
                                    placeholder="Sélectionner un commercial"
                                />
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup className="mb-0">
                                <Label>Date de référence</Label>
                                <Input
                                    type="date"
                                    value={filters.refDate}
                                    onChange={(e) => setFilters((f) => ({ ...f, refDate: e.target.value }))}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={2} className="d-flex gap-2">
                            <Button color="primary" onClick={handleFilter} disabled={loading}>
                                {loading ? <Spinner size="sm" /> : <FaFilter className="me-1" />}
                                Filtrer
                            </Button>
                            <Button color="dark" onClick={handleExport} disabled={exportLoading}>
                                {exportLoading ? <Spinner size="sm" /> : <><FaDownload className="me-1" /> Export</>}
                            </Button>
                        </Col>
                    </Row>

                    <div className="table-responsive">
                        {ledgerLoading || loading ? (
                            <div className="text-center py-5">
                                <Spinner color="primary" />
                                <div className="mt-2 text-muted">Chargement...</div>
                            </div>
                        ) : (
                            <Table bordered striped>
                                <thead className="table-light">
                                    <tr>
                                        <th>#</th>
                                        <th>Commercial</th>
                                        <th>Période</th>
                                        <th>Statut</th>
                                        <th>Base</th>
                                        <th>Commission base</th>
                                        <th>Bonus</th>
                                        <th>Total</th>
                                        <th>Posté le</th>
                                        <th>Ref</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data && data.length > 0 ? (
                                        data.map((r, idx) => (
                                            <tr key={r.id || idx}>
                                                <td>{idx + 1}</td>
                                                <td>{r.commercialName}</td>
                                                <td>{r.periodStart} → {r.periodEnd}</td>
                                                <td>
                                                    {r.status === "POSTED" ? (
                                                        <span className="badge bg-success">POSTED</span>
                                                    ) : (
                                                        <span className="badge bg-secondary">REVERSED</span>
                                                    )}
                                                </td>
                                                <td>{formatPrice(r.baseTotal)}</td>
                                                <td>{formatPrice(r.baseCommission)}</td>
                                                <td>{formatPrice(r.bonusCommission)}</td>
                                                <td><strong>{formatPrice(r.totalCommission)}</strong></td>
                                                <td>{r.postedAt?.replace("T", " ").slice(0, 16)}</td>
                                                <td>{r.postingRef}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={10} className="text-center text-muted py-4">
                                                Aucun enregistrement trouvé
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        )}
                    </div>
                </CardBody>
            </Card>
            {chartData.length > 0 && (
                <Card className="shadow-sm mb-3">
                    <CardBody>
                        <div className="d-flex align-items-center mb-2">
                            <FaChartBar className="text-primary me-2" />
                            <h5 className="mb-0">Synthèse des commissions (POSTED) par commercial</h5>
                        </div>
                        <div className="mb-2">
                            <strong>Total POSTED:</strong> {formatPrice(totalPosted)}
                        </div>


                        <div style={{ width: "100%", height: 320 }}>
                            <ResponsiveContainer>
                                <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="commercial"
                                        interval={0}
                                        angle={-20}
                                        textAnchor="end"
                                        height={60}
                                    />
                                    <YAxis
                                        tickFormatter={(v) =>
                                            v >= 1000000
                                                ? (v / 1000000).toFixed(1) + "M"
                                                : v >= 1000
                                                    ? (v / 1000).toFixed(0) + "k"
                                                    : v
                                        }
                                        width={70}
                                        tick={{ fontSize: 12, fill: "#555" }}
                                    />
                                    <Tooltip
                                        formatter={(value, name) => [formatPrice(value), name]}
                                        labelFormatter={(label) => `Commercial: ${label}`}
                                    />
                                    <Legend />
                                    <Bar dataKey="baseCommission" stackId="a" name="Commission base" fill="#007BFF" />
                                    <Bar dataKey="bonusCommission" stackId="a" name="Bonus" fill="#FFC107" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <small className="text-muted">
                            Données agrégées sur la période filtrée ({filters.dateStart} → {filters.dateEnd}). POSTED uniquement.
                        </small>
                    </CardBody>
                </Card>
            )}

        </Container>
    );
};

export default CommissionHistoryPage;
